import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const CoreIntelligenceService = () => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState('overview');

  const features = [
    {
      id: 'neural',
      title: 'Neural Processing Engine',
      icon: 'memory',
      description: 'Advanced neural networks that power intelligent decision-making and pattern recognition across your enterprise.',
      benefits: ['Real-time processing', 'Adaptive learning', 'Scalable architecture', 'Multi-modal AI'],
      metrics: { processing: '1M ops/sec', accuracy: '99.7%', latency: '<10ms' }
    },
    {
      id: 'knowledge',
      title: 'Knowledge Graph System',
      icon: 'account_tree',
      description: 'Comprehensive knowledge representation that connects data, relationships, and insights across your organization.',
      benefits: ['Semantic understanding', 'Relationship mapping', 'Context awareness', 'Dynamic updates'],
      metrics: { entities: '10M+ entities', connections: '100M+ links', queries: 'Sub-second' }
    },
    {
      id: 'reasoning',
      title: 'Automated Reasoning',
      icon: 'psychology',
      description: 'Sophisticated reasoning capabilities that enable complex problem-solving and strategic decision support.',
      benefits: ['Logical inference', 'Causal analysis', 'Predictive modeling', 'Risk assessment'],
      metrics: { accuracy: '95%', complexity: 'Multi-step', speed: 'Real-time' }
    },
    {
      id: 'learning',
      title: 'Continuous Learning',
      icon: 'auto_fix_high',
      description: 'Self-improving AI systems that adapt and evolve based on new data and changing business requirements.',
      benefits: ['Adaptive algorithms', 'Performance optimization', 'Bias detection', 'Model evolution'],
      metrics: { improvement: '+15% monthly', adaptation: 'Automatic', monitoring: '24/7' }
    }
  ];

  const architectureComponents = [
    {
      title: 'Distributed Computing',
      description: 'Scalable infrastructure that processes workloads across multiple nodes',
      icon: 'hub',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Edge Processing',
      description: 'Low-latency processing at the edge for real-time applications',
      icon: 'router',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Quantum-Ready',
      description: 'Future-proof architecture designed for quantum computing integration',
      icon: 'science',
      color: 'from-purple-500 to-violet-500'
    },
    {
      title: 'Security Layer',
      description: 'Enterprise-grade security with encryption and access controls',
      icon: 'security',
      color: 'from-red-500 to-pink-500'
    }
  ];

  const useCases = [
    {
      title: 'Enterprise Decision Support',
      description: 'AI-powered insights for strategic planning, risk management, and operational optimization.',
      icon: 'analytics',
      industries: ['Finance', 'Consulting', 'Manufacturing']
    },
    {
      title: 'Research & Development',
      description: 'Accelerate innovation with AI-assisted research, hypothesis generation, and experimental design.',
      icon: 'biotech',
      industries: ['Pharmaceuticals', 'Technology', 'Academia']
    },
    {
      title: 'Predictive Maintenance',
      description: 'Prevent equipment failures and optimize maintenance schedules using predictive analytics.',
      icon: 'build',
      industries: ['Manufacturing', 'Energy', 'Transportation']
    },
    {
      title: 'Fraud Detection',
      description: 'Real-time fraud detection and prevention using advanced pattern recognition and anomaly detection.',
      icon: 'shield',
      industries: ['Banking', 'Insurance', 'E-commerce']
    }
  ];

  const technicalSpecs = [
    { metric: 'Processing Power', value: '100+ TFLOPS', icon: 'speed' },
    { metric: 'Memory Capacity', value: '1TB+ RAM', icon: 'memory' },
    { metric: 'Storage', value: 'Petabyte Scale', icon: 'storage' },
    { metric: 'Network', value: '100Gbps+', icon: 'network_check' },
    { metric: 'Availability', value: '99.99% SLA', icon: 'verified' },
    { metric: 'Security', value: 'SOC2 Type II', icon: 'security' }
  ];

  const integrations = [
    { name: 'TensorFlow', icon: 'psychology', category: 'ML Framework' },
    { name: 'Apache Spark', icon: 'flash_on', category: 'Big Data' },
    { name: 'Kubernetes', icon: 'cloud', category: 'Orchestration' },
    { name: 'PostgreSQL', icon: 'storage', category: 'Database' },
    { name: 'Redis', icon: 'memory', category: 'Caching' },
    { name: 'Elasticsearch', icon: 'search', category: 'Search Engine' }
  ];

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center">
                <span className="material-symbols-outlined text-blue-400 text-3xl">memory</span>
              </div>
              <div className="text-left">
                <h1 className="text-4xl md:text-6xl font-bold text-white">
                  Core <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Intelligence</span>
                </h1>
                <p className="text-gray-400 text-lg mt-2">Enterprise AI Brain & Neural Architecture</p>
              </div>
            </div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              The foundational intelligence layer that powers all OrbitMind services. Advanced neural processing, 
              knowledge graphs, and reasoning engines that transform raw data into actionable business intelligence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105">
                Explore Architecture
              </button>
              <button className="border border-gray-600 hover:border-blue-500 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all">
                Technical Docs
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Components */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Architecture Components</h2>
            <p className="text-gray-300 text-lg">Built on cutting-edge infrastructure for maximum performance</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {architectureComponents.map((component, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-blue-500/50 transition-all group">
                <div className={`w-12 h-12 bg-gradient-to-r ${component.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <span className="material-symbols-outlined text-white text-2xl">{component.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{component.title}</h3>
                <p className="text-gray-300 text-sm">{component.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Technical Specifications</h2>
            <p className="text-gray-300 text-lg">Enterprise-grade performance and reliability</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technicalSpecs.map((spec, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-blue-500/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-blue-400">{spec.icon}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{spec.metric}</h3>
                    <p className="text-blue-400 font-bold text-lg">{spec.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Navigation */}
      <section className="py-12 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {features.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setSelectedFeature(feature.id)}
                className={`flex items-center gap-3 px-6 py-3 rounded-lg transition-all ${
                  selectedFeature === feature.id
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="material-symbols-outlined">{feature.icon}</span>
                <span className="font-medium">{feature.title}</span>
              </button>
            ))}
          </div>

          {/* Feature Details */}
          {features.map((feature) => (
            selectedFeature === feature.id && (
              <div key={feature.id} className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-blue-400 text-2xl">{feature.icon}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white">{feature.title}</h3>
                    </div>
                    <p className="text-gray-300 text-lg mb-6">{feature.description}</p>
                    <div className="space-y-3">
                      <h4 className="text-white font-semibold">Core Capabilities:</h4>
                      <ul className="space-y-2">
                        {feature.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-center gap-3 text-gray-300">
                            <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full"></span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-6">
                    <h4 className="text-white font-semibold mb-4">Performance Metrics</h4>
                    <div className="grid grid-cols-1 gap-4">
                      {Object.entries(feature.metrics).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-3 bg-gray-800 rounded-lg">
                          <span className="text-gray-400 capitalize">{key}</span>
                          <span className="text-blue-400 font-bold">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Enterprise Applications</h2>
            <p className="text-gray-300 text-lg">Powering intelligent solutions across industries</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-blue-500/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-blue-400 text-2xl">{useCase.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">{useCase.title}</h3>
                </div>
                <p className="text-gray-300 mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.industries.map((industry, idx) => (
                    <span key={idx} className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 text-blue-400 px-3 py-1 rounded-full text-sm border border-blue-500/20">
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Technology Stack</h2>
            <p className="text-gray-300 text-lg">Built on industry-leading technologies and frameworks</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {integrations.map((integration, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-4 text-center hover:bg-gray-700 transition-all group">
                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-gradient-to-r group-hover:from-blue-500/20 group-hover:to-cyan-500/20 transition-all">
                  <span className="material-symbols-outlined text-blue-400">{integration.icon}</span>
                </div>
                <h4 className="text-white font-medium text-sm mb-1">{integration.name}</h4>
                <p className="text-gray-400 text-xs">{integration.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-500/10 to-cyan-500/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Power Your Enterprise with AI</h2>
          <p className="text-xl text-gray-300 mb-8">
            Deploy the most advanced AI infrastructure to transform your business operations and decision-making processes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105"
            >
              Deploy Now
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border border-blue-500 hover:bg-gradient-to-r hover:from-blue-500 hover:to-cyan-500 text-blue-400 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all"
            >
              Enterprise Consultation
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoreIntelligenceService;